import { Card, Group, Loader, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useOrganizationCost } from '@/api/cost';
import { errorMessage } from '@/api/errors';
import { useFormat } from '@/components/useFormat';
import { brand } from '@/theme/theme';
import { CostFigure } from './ProjectCostCard';

/** The organization's storage (N21): the sum of its projects' latest inventories and each one's share. */
export function OrganizationCostCard({ orgId }: { orgId: string }) {
  const { t } = useTranslation('ops');
  const { bytes, number, dateTime } = useFormat();
  const cost = useOrganizationCost(orgId);
  const rows = [...(cost.data?.projects ?? [])].sort((a, b) => b.total - a.total);

  return (
    <Card data-testid="organization-cost">
      <Group justify="space-between" mb="sm">
        <Title order={3}>{t('cost.title')}</Title>
      </Group>
      {cost.isPending ? (
        <Loader size="sm" color="forest" />
      ) : cost.isError ? (
        <Text fz={14} c="red">
          {errorMessage(cost.error)}
        </Text>
      ) : rows.length === 0 ? (
        <Text fz={13} c={brand.textMuted}>
          {t('cost.never')}
        </Text>
      ) : (
        <Stack gap="md">
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
            <CostFigure label={t('cost.total')} value={bytes(cost.data.total)} />
            <CostFigure label={t('cost.raw')} value={bytes(cost.data.raw)} />
            <CostFigure label={t('cost.pipeline')} value={bytes(cost.data.pipeline)} />
          </SimpleGrid>
          <div style={{ overflowX: 'auto' }}>
            <Table fz={13}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('cost.project')}</Table.Th>
                  <Table.Th ta="right">{t('cost.total')}</Table.Th>
                  <Table.Th ta="right">{t('cost.share')}</Table.Th>
                  <Table.Th ta="right">{t('cost.raw')}</Table.Th>
                  <Table.Th ta="right">{t('cost.pipeline')}</Table.Th>
                  <Table.Th ta="right">{t('cost.missingColumn')}</Table.Th>
                  <Table.Th>{t('cost.inventory')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((p) => (
                  <Table.Tr key={p.project}>
                    <Table.Td>
                      <Link to="/staff/projects/$projectId" params={{ projectId: p.project }} style={{ fontWeight: 600, color: brand.text, textDecoration: 'none' }}>
                        {p.name}
                      </Link>
                    </Table.Td>
                    <Table.Td ta="right">{bytes(p.total)}</Table.Td>
                    <Table.Td ta="right">{cost.data.total > 0 ? `${number((100 * p.total) / cost.data.total, 1)}\u00a0%` : '—'}</Table.Td>
                    <Table.Td ta="right">{bytes(p.raw)}</Table.Td>
                    <Table.Td ta="right">{bytes(p.pipeline)}</Table.Td>
                    <Table.Td ta="right" c={p.missing > 0 ? 'red' : undefined}>
                      {p.missing}
                    </Table.Td>
                    <Table.Td>{dateTime(p.taken_at)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </div>
        </Stack>
      )}
    </Card>
  );
}
